import PropTypes from 'prop-types';
import { Box, Stack, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';

export interface CardProps {
  icon: string | string[];
  title: string;
  description: string;
}

const Card = ({ icon, title, description }: CardProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        p: 2,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: '48px', height: '48px', position: 'relative' }}>
        {Array.isArray(icon) ? (
          <>
            {icon.map((img, idx) => (
              <Box
                key={idx}
                component='img'
                src={img}
                alt=''
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                }}
              />
            ))}
          </>
        ) : (
          <Box
            component='img'
            src={icon}
            alt=''
            sx={{
              width: '100%',
              height: '100%',
            }}
          />
        )}
      </Box>
      <Typography
        variant='subtitle4'
        sx={{
          color: globalColours['$global-colour-content-primary'],
        }}
      >
        {title}
      </Typography>
      <Typography
        variant='body2'
        sx={{
          color: globalColours['$global-colour-content-primary'],
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

export interface CardClusterProps {
  className?: string;
  heading?: string;
  subheading?: string;
  cards: CardProps[];
}

export const CardCluster = ({ className, heading, subheading, cards }: CardClusterProps) => {
  return (
    <Box
      className={className}
      sx={{
        background: globalColours['$global-colour-palette-gray-50'],
        px: 0,
        py: { xs: 4, md: 4 },
      }}
    >
      <Container>
        {/* Heading */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pb: { xs: 2, md: 4 },
          }}
        >
          <Stack
            sx={{
              gap: 1.5,
              alignItems: 'center',
              py: 2,
              borderRadius: '16px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant='subtitle1'
              sx={{
                color: globalColours['$global-colour-content-primary'],
                textAlign: 'center',
              }}
            >
              {heading}
            </Typography>
            <Typography
              variant='body2SemiBold'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                maxWidth: { xs: '100%', md: '500px' },
                textAlign: 'center',
              }}
            >
              {subheading}
            </Typography>
          </Stack>
        </Box>

        {/* Cards Grid */}
        <Stack
          sx={{
            gap: { xs: 1.5, md: 4 },
          }}
        >
          {/* First Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{
              gap: { xs: 1.5, md: 4 },
            }}
          >
            {cards.slice(0, 3).map((card, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: '1 1 auto', md: '1 0 0' },
                  minWidth: { xs: 'auto', md: '0px' },
                }}
              >
                <Card {...card} />
              </Box>
            ))}
          </Stack>

          {/* Second Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{
              gap: { xs: 1.5, md: 4 },
            }}
          >
            {cards.slice(3, 6).map((card, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: '1 1 auto', md: '1 0 0' },
                  minWidth: { xs: 'auto', md: '0px' },
                }}
              >
                <Card {...card} />
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

CardCluster.propTypes = {
  className: PropTypes.string,
};

CardCluster.defaultProps = {
  className: null,
};

export default styled<any>(CardCluster)(({ theme }) => ``);
